import { Delete, Add } from '@mui/icons-material';
import { Box, Grid, InputAdornment, Button, Divider, Typography } from '@mui/material';
import { useFieldArray, useFormContext } from 'react-hook-form';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import { Colors } from 'src/shared/components/styles/colors';
import { useTranslation } from 'react-i18next';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { useGlobals } from 'src/shared/contexts/appContext';
import { CreateInvoiceArticlePicker } from './createInvoiceArticlePicker';

export const CreateInvoiceArticleRows = () => {
  const { control, watch } = useFormContext();

  const { t } = useTranslation(['invoice']);

  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'rows',
  });

  // Watch the fields for changes
  const watchedFields = watch('rows', fields);

  const displaySum = () => {
    let sum = 0;
    watchedFields.forEach((field) => {
      sum += +field.amount || 0;
    });
    return sum;
  };

  return (
    <FormFieldset legend={t('invoice:createInvoice.articleCount', { count: fields.length })}>
      <Box sx={{ p: 1.5, pt: 2.5 }}>
        {fields.map((item, index) => (
          // eslint-disable-next-line react/no-array-index-key
          <Box sx={{ mb: 2 }} key={index}>
            <GridWrapper narrow>
              <Grid item xs={12} md={4}>
                <CreateInvoiceArticlePicker
                  name={`rows.${index}.articleNr`}
                  label={t('invoice:createInvoice.invoiceRow.articleName')}
                  initialValue=''
                  textInputReference={`rows.${index}.text`}
                  index={index}
                />
              </Grid>
              <Grid item xs={7} md={5}>
                <ControlledTextField name={`rows.${index}.text`} label={t('invoice:createInvoice.invoiceRow.text')} />
              </Grid>

              <Grid item xs={5} md={3}>
                <ControlledTextField
                  name={`rows.${index}.amount`}
                  label={t('invoice:createInvoice.invoiceRow.amount')}
                  type='number'
                  inputProps={{
                    min: 0,
                  }}
                  // eslint-disable-next-line react/jsx-no-duplicate-props
                  InputProps={{
                    endAdornment: (
                      <>
                        <InputAdornment position='end'>{currency}</InputAdornment>
                        {(fields.length > 1 || (index === 0 && fields.length > 1)) && (
                          <InputAdornment position='end' sx={{ width: '4rem' }}>
                            <Button
                              sx={{
                                position: 'absolute',
                                right: 0,
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minWidth: '3rem !important',
                                width: '3rem',
                                height: '100%',
                                backgroundColor: `${Colors.PageBackground}`,
                                borderLeft: (thm) => `1px solid ${thm.palette.divider}`,
                                borderRadius: 0,
                              }}
                              onClick={() => remove(index)}
                            >
                              <Delete />
                            </Button>
                          </InputAdornment>
                        )}
                      </>
                    ),
                  }}
                />
              </Grid>
              {index + 1 !== fields.length && (
                <Grid item xs={12} sx={{ display: { xs: 'block', md: 'none' } }}>
                  <Divider sx={{ mx: -0.5 }} />
                </Grid>
              )}
            </GridWrapper>
          </Box>
        ))}

        <GridWrapper>
          <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button size='small' variant='outlined' endIcon={<Add />} onClick={() => append({ amount: 0, text: '', articleNr: 0 })}>
              {t('common:button.add')}
            </Button>
          </Grid>
        </GridWrapper>
        <Typography component='div' sx={{ display: 'flex', justifyContent: 'flex-end', fontWeight: 600, pt: 1 }}>
          {t('invoice:createInvoice.articleSum')}: <AmountWithCurrency amount={displaySum()} decimals={2} />
        </Typography>
      </Box>
    </FormFieldset>
  );
};
